import { useState, useEffect } from 'react';
import { collection, doc, getDoc, getDocs, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Store, Search, Eye, AlertTriangle, X, Users, Trash2, Package } from 'lucide-react';
import DisputeManagement from '../../components/orders/DisputeManagement';
import type { Order } from '../../types/orders';
import { calculateCommission } from '../../utils/commissionUtils';
import { sendToWarehouse } from '../../utils/warehouseUtils';
import { toast } from 'react-hot-toast';

export default function MarketplaceOrders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<string | null>(null);
  const [selectedSellers, setSelectedSellers] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 10;
  const [sendingToWarehouse, setSendingToWarehouse] = useState<string | null>(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      loadOrders();
    } else {
      navigate('/');
    }
  }, [currentUser]);

  const getTimestamp = (date: any): number => {
    if (!date) return 0;
    
    try {
      // Se for um Timestamp do Firestore
      if (date && typeof date.toDate === 'function') {
        return date.toDate().getTime();
      }
      // Se for uma string, converter para Date
      else if (typeof date === 'string') {
        return new Date(date).getTime();
      }
      // Se já for um Date
      else if (date instanceof Date) {
        return date.getTime();
      }
      return 0;
    } catch (error) {
      console.error('Erro ao converter data:', error);
      return 0;
    }
  };

  const loadOrders = async () => {
    try {
      const ordersRef = collection(db, 'orders');
      const snapshot = await getDocs(ordersRef);
      
      const ordersPromises = snapshot.docs.map(async (orderDoc) => {
        try {
          const data = orderDoc.data();
          let buyerName = data.customer?.name || 'N/A';
          let sellers: { id: string; name: string }[] = [];

          // Para pedidos WooCommerce
          if (data.source === 'woocommerce' && data.sourceData) {
            buyerName = `${data.sourceData.billing?.first_name || ''} ${data.sourceData.billing?.last_name || ''}`.trim() || 
                       `${data.sourceData.shipping?.first_name || ''} ${data.sourceData.shipping?.last_name || ''}`.trim() ||
                       data.customer?.name || 'N/A';

            data.orderNumber = data.sourceData.number || data.sourceOrderId || '';
            data.total = parseFloat(data.sourceData.total) || 0;
            data.subtotal = parseFloat(data.sourceData.subtotal) || 0;
            data.isOnlineSale = true;
          }

          // Se for um pedido de convidado, usar o nome do endereço de entrega
          if (data.isGuestOrder && data.shippingAddress?.name) {
            buyerName = data.shippingAddress.name;
          }

          // Obter nome do comprador se ainda não estiver definido
          if (buyerName === 'N/A' && data.buyerId) {
            try {
              const buyerDoc = await getDoc(doc(db, 'users', data.buyerId));
              if (buyerDoc.exists()) {
                const buyerData = buyerDoc.data();
                buyerName = buyerData.displayName || buyerData.email || 'N/A';
              }
            } catch (error) {
              console.error('Erro ao buscar dados do comprador:', error);
            }
          }

          // Obter vendedores dos itens ou companyId
          if (data.source === 'woocommerce' && data.companyId) {
            try {
              const companyDoc = await getDoc(doc(db, 'companies', data.companyId));
              if (companyDoc.exists()) {
                const companyData = companyDoc.data();
                sellers.push({
                  id: data.companyId,
                  name: companyData.commercialName || companyData.legalName || 'N/A'
                });
              }
            } catch (error) {
              console.error('Erro ao buscar dados da empresa:', error);
            }
          } else if (data.items?.length > 0) {
            const uniqueCompanyIds = [...new Set(data.items.map((item: any) => item.companyId).filter((id: string | undefined): id is string => !!id))];
            for (const companyId of uniqueCompanyIds) {
              try {
                const companyDoc = await getDoc(doc(db, 'companies', companyId));
                if (companyDoc.exists()) {
                  const companyData = companyDoc.data();
                  sellers.push({
                    id: companyId,
                    name: companyData.commercialName || companyData.legalName || 'N/A'
                  });
                }
              } catch (error) {
                console.error(`Erro ao buscar dados da empresa ${companyId}:`, error);
              }
            }
          }

          // Se não encontrou vendedores mas tem sellerId ou companyId, tentar usar
          if (sellers.length === 0 && (data.sellerId || data.companyId)) {
            const companyId = data.sellerId || data.companyId;
            try {
              const companyDoc = await getDoc(doc(db, 'companies', companyId));
              if (companyDoc.exists()) {
                const companyData = companyDoc.data();
                sellers.push({
                  id: companyId,
                  name: companyData.commercialName || companyData.legalName || 'N/A'
                });
              }
            } catch (error) {
              console.error('Erro ao buscar dados do vendedor:', error);
            }
          }

          // Se ainda não encontrou vendedores e é uma venda manual, tentar buscar da empresa do usuário que criou
          if (sellers.length === 0 && data.source === 'manual') {
            // Primeiro, tentar usar o companyId da ordem
            if (data.companyId) {
              try {
                const companyDoc = await getDoc(doc(db, 'companies', data.companyId));
                if (companyDoc.exists()) {
                  const companyData = companyDoc.data();
                  sellers.push({
                    id: data.companyId,
                    name: companyData.commercialName || companyData.legalName || 'N/A'
                  });
                }
              } catch (error) {
                console.error('Erro ao buscar dados da empresa da venda manual:', error);
              }
            }
            
            // Se ainda não encontrou e tem createdBy, tentar buscar da empresa do usuário
            if (sellers.length === 0 && data.createdBy) {
              try {
                const userDoc = await getDoc(doc(db, 'users', data.createdBy));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  if (userData.companyId) {
                    const companyDoc = await getDoc(doc(db, 'companies', userData.companyId));
                    if (companyDoc.exists()) {
                      const companyData = companyDoc.data();
                      sellers.push({
                        id: userData.companyId,
                        name: companyData.commercialName || companyData.legalName || 'N/A'
                      });
                    }
                  }
                }
              } catch (error) {
                console.error('Erro ao buscar dados da empresa do criador:', error);
              }
            }

            // Se ainda não encontrou e tem sellerId, tentar usar como última opção
            if (sellers.length === 0 && data.sellerId) {
              try {
                const companyDoc = await getDoc(doc(db, 'companies', data.sellerId));
                if (companyDoc.exists()) {
                  const companyData = companyDoc.data();
                  sellers.push({
                    id: data.sellerId,
                    name: companyData.commercialName || companyData.legalName || 'N/A'
                  });
                }
              } catch (error) {
                console.error('Erro ao buscar dados do vendedor da venda manual:', error);
              }
            }
          }

          // Determinar o tipo de ordem
          const isOnlineSale = data.source === 'woocommerce' || data.isOnlineSale || false;
          const isMarketplace = data.source === 'marketplace' || data.isMarketplace || false;
          const isPrivateSale = data.isPrivateSale || false;
          const isManual = data.source === 'manual' || false;

          const orderData: Order = {
            id: orderDoc.id,
            orderNumber: data.orderNumber || '',
            source: data.source || '',
            sourceOrderId: data.sourceOrderId || '',
            sourceData: data.sourceData,
            createdAt: data.createdAt,
            status: data.status || 'pending',
            buyerId: data.buyerId,
            buyerName,
            sellerId: data.sellerId,
            companyId: data.companyId || '',
            customer: data.customer,
            shippingAddress: data.shippingAddress,
            items: data.items || [],
            sellers,
            subtotal: data.subtotal || 0,
            shippingCost: data.shippingCost || 0,
            total: data.total || 0,
            commission: 0,
            isOnlineSale,
            isPrivateSale,
            isMarketplace,
            isManual,
            displayDate: formatDate(data.createdAt),
            createdBy: data.createdBy || '',
            paymentMethod: data.paymentMethod || '',
            comments: data.comments || [],
            dispute: data.dispute,
            useWallet: data.useWallet,
            walletAmount: data.walletAmount,
            onlineShop: data.onlineShop,
            invoicePdf: data.invoicePdf,
            invoiceStatus: data.invoiceStatus,
            privateSaleInvoices: data.privateSaleInvoices,
            warehouseStatus: data.warehouseStatus || '',
            warehouseOrderId: data.warehouseOrderId || '',
            warehouseSentAt: data.warehouseSentAt || ''
          };

          return orderData;
        } catch (error) {
          console.error('Erro ao processar encomenda:', error);
          return null;
        }
      });

      const ordersData = (await Promise.all(ordersPromises)).filter((order): order is Order => order !== null);
      
      // Ordenar ordens por data de criação, mais recentes primeiro
      const sortedOrders = ordersData.sort((a, b) => {
        try {
          const getTimestamp = (date: any) => {
            if (!date) return 0;
            if (typeof date.toDate === 'function') return date.toDate().getTime();
            if (typeof date === 'string') return new Date(date).getTime();
            if (date instanceof Date) return date.getTime();
            return 0;
          };

          return getTimestamp(b.createdAt) - getTimestamp(a.createdAt);
        } catch (error) {
          console.error('Erro ao ordenar datas:', error);
          return 0;
        }
      });

      setOrders(sortedOrders);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar encomendas:', error);
      setError('Ocorreu um erro ao carregar as encomendas. Por favor, tente novamente.');
      setLoading(false);
    }
  };

  const formatDate = (date: any): string => {
    if (!date) return 'N/A';
    
    try {
      // Se for um Timestamp do Firestore
      if (date && typeof date.toDate === 'function') {
        date = date.toDate();
      }
      // Se for uma string, converter para Date
      else if (typeof date === 'string') {
        date = new Date(date);
      }
      
      // Verificar se a data é válida
      if (isNaN(date.getTime())) {
        return 'N/A';
      }

      // Formatar a data no estilo português
      return date.toLocaleDateString('pt-PT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return 'N/A';
    }
  };

  const getOrderNumber = (order: Order) => {
    return order.orderNumber || order.sourceOrderId || order.id;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'shipped': return 'bg-purple-100 text-purple-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'awaiting_payment': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      // Estados básicos
      case 'pending': return 'Pendente';
      case 'processing': return 'Em Processamento';
      case 'shipped': return 'Enviada';
      case 'delivered': return 'Entregue';
      case 'cancelled': return 'Cancelada';
      case 'awaiting_payment': return 'Aguardando Pagamento';
      
      // Estados do WooCommerce e Marketplace
      case 'preparation': return 'Em Preparação';
      case 'awaitingstock': return 'Aguardando Stock';
      case 'awaitingpickup': return 'Aguardando Recolha';
      case 'returned': return 'Devolvida';
      case 'on-hold': return 'Em Espera';
      case 'completed': return 'Concluída';
      case 'refunded': return 'Reembolsada';
      case 'failed': return 'Falhou';
      case 'trash': return 'Lixeira';
      case 'pronto-para-envio': return 'Pronto para Envio';
      case 'dados-errados': return 'Dados Errados';
      case 'checkout-draft': return 'Rascunho';
      
      default: return status;
    }
  };

  const getOrderType = (order: Order) => {
    if (order.source === 'marketplace' || order.isMarketplace) return 'Marketplace';
    if (order.source === 'woocommerce' || order.isOnlineSale) return 'Loja Online';
    if (order.isPrivateSale) return 'Venda Privada';
    if (order.isManual || order.source === 'manual') return 'Venda Manual';
    return 'N/A';
  };

  const getOrderTypeColor = (order: Order) => {
    if (order.source === 'marketplace' || order.isMarketplace) return 'bg-purple-100 text-purple-800';
    if (order.source === 'woocommerce' || order.isOnlineSale) return 'bg-blue-100 text-blue-800';
    if (order.isPrivateSale) return 'bg-yellow-100 text-yellow-800';
    if (order.isManual || order.source === 'manual') return 'bg-gray-100 text-gray-800';
    return 'bg-gray-100 text-gray-800';
  };

  const handleDeleteOrder = async (orderId: string) => {
    if (!window.confirm('Tem certeza que deseja apagar esta encomenda? Esta ação não pode ser desfeita.')) {
      return;
    }

    setDeleteLoading(orderId);
    try {
      await deleteDoc(doc(db, 'orders', orderId));
      setOrders(orders.filter(order => order.id !== orderId));
    } catch (error) {
      console.error('Erro ao apagar encomenda:', error);
      setError('Ocorreu um erro ao apagar a encomenda. Por favor, tente novamente.');
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleSendToWarehouse = async (order: Order) => {
    if (!order.id) return;
    
    setSendingToWarehouse(order.id);
    try {
      const result = await sendToWarehouse(order);
      
      if (result.success) {
        // Atualizar o documento com a informação de que foi enviado para o armazém
        await updateDoc(doc(db, 'orders', order.id), {
          warehouseStatus: 'sent',
          warehouseOrderId: result.orderId,
          warehouseSentAt: new Date().toISOString()
        });

        // Atualizar o estado local
        setOrders(orders.map(o => {
          if (o.id === order.id) {
            return {
              ...o,
              warehouseStatus: 'sent',
              warehouseOrderId: result.orderId,
              warehouseSentAt: new Date().toISOString()
            };
          }
          return o;
        }));

        toast.success(`Pedido enviado para o armazém com sucesso! Nº do pedido Baselinker: ${result.orderId}`);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Erro ao enviar para o armazém:', error);
      toast.error('Erro ao enviar para o armazém');
    } finally {
      setSendingToWarehouse(null);
    }
  };

  const renderWarehouseStatus = (order: Order) => {
    if (order.warehouseStatus === 'sent') {
      return (
        <div className="flex items-center space-x-2">
          <Package className="w-4 h-4 text-green-500" />
          <span className="text-sm text-green-600">
            Enviado para armazém
            {order.warehouseOrderId && (
              <span className="ml-1 font-medium">
                (Pedido Baselinker: {order.warehouseOrderId})
              </span>
            )}
          </span>
        </div>
      );
    }
    return null;
  };

  const filteredOrders = orders.filter(order =>
    order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.buyerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.sellers?.some(seller => seller.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Cálculos para paginação
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-secondary flex items-center gap-2">
              <Store className="text-primary" />
              Todas as Encomendas
            </h2>
          </div>

          <div className="mb-6 relative">
            <input
              type="text"
              placeholder="Pesquisar por número, cliente ou vendedor..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          </div>

          {error && (
            <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
              {error}
            </div>
          )}

          {filteredOrders.length === 0 && !loading && (
            <div className="text-center py-8 text-gray-500">
              Nenhuma encomenda encontrada
            </div>
          )}

          <div className="overflow-x-auto -mx-6 sm:mx-0">
            <div className="inline-block min-w-full align-middle">
              <div className="overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Nº / Data
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cliente
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Vendedor
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Total / Estado
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentOrders.map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {getOrderNumber(order)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {order.displayDate}
                          </div>
                          <span className={`mt-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getOrderTypeColor(order)}`}>
                            {getOrderType(order)}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-col">
                            <div className="text-sm font-medium text-gray-900 line-clamp-1">{order.buyerName}</div>
                            {order.customer?.email && (
                              <div className="text-xs text-gray-500 line-clamp-1">{order.customer.email}</div>
                            )}
                            {order.customer?.phone && (
                              <div className="text-xs text-gray-500">{order.customer.phone}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-col">
                            {order.sellers?.map((seller, index) => (
                              <span key={seller.id} className="text-sm line-clamp-1">
                                {seller.name}
                                {index < (order.sellers?.length || 0) - 1 ? ', ' : ''}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-sm font-medium text-gray-900">€{order.total?.toFixed(2)}</div>
                          <span className={`mt-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(order.status)}`}>
                            {getStatusText(order.status)}
                          </span>
                          {renderWarehouseStatus(order)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => navigate(`/dashboard/orders/${order.id}`)}
                              className="text-primary hover:text-primary-dark"
                              title="Ver detalhes"
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              onClick={() => setSelectedSellers(order.id)}
                              className="text-blue-600 hover:text-blue-800"
                              title="Ver vendedores"
                            >
                              <Users size={18} />
                            </button>
                            {order.dispute && (
                              <button
                                onClick={() => setSelectedOrder(order.id)}
                                className="text-red-600 hover:text-red-800"
                                title="Gerir reclamação"
                              >
                                <AlertTriangle size={18} />
                              </button>
                            )}
                            <button
                              onClick={() => handleSendToWarehouse(order)}
                              disabled={sendingToWarehouse === order.id}
                              className="text-gray-600 hover:text-blue-600 disabled:text-gray-400"
                              title="Enviar para Armazém"
                            >
                              <Package size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteOrder(order.id)}
                              className="text-red-600 hover:text-red-800"
                              title="Apagar encomenda"
                              disabled={deleteLoading === order.id}
                            >
                              {deleteLoading === order.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                              ) : (
                                <Trash2 size={18} />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Paginação */}
                {totalPages > 1 && (
                  <div className="flex justify-center space-x-2 mt-4 pb-4">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-3 py-1 rounded-md ${
                        currentPage === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                      }`}
                    >
                      Anterior
                    </button>
                    
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      // Mostrar apenas 5 páginas por vez
                      if (
                        pageNumber === 1 ||
                        pageNumber === totalPages ||
                        (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => handlePageChange(pageNumber)}
                            className={`px-3 py-1 rounded-md ${
                              currentPage === pageNumber
                                ? 'bg-primary text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                            }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      } else if (
                        (pageNumber === currentPage - 2 && pageNumber > 2) ||
                        (pageNumber === currentPage + 2 && pageNumber < totalPages - 1)
                      ) {
                        return <span key={pageNumber}>...</span>;
                      }
                      return null;
                    })}

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1 rounded-md ${
                        currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                      }`}
                    >
                      Próxima
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sellers Modal */}
      {selectedSellers && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Vendedores</h3>
              <button
                onClick={() => setSelectedSellers(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X size={20} />
              </button>
            </div>
            <ul className="space-y-2">
              {orders.find(o => o.id === selectedSellers)?.sellers.map((seller, index) => (
                <li key={seller.id} className="text-sm text-gray-700 py-2 border-b last:border-0">
                  {seller.name}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Dispute Management Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">Gestão de Reclamação</h3>
              <button
                onClick={() => setSelectedOrder(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X size={20} />
              </button>
            </div>
            <DisputeManagement 
              orderId={selectedOrder} 
              onDisputeUpdate={() => {
                loadOrders();
                setSelectedOrder(null);
              }} 
            />
          </div>
        </div>
      )}
    </div>
  );
}